import { ApiErrorCodeLabelModifier } from '@/api/error-codes';
import { Box, ErrorContent, Flex } from '@/components';
import { useHandle403ErrorWhenSearching as useHandle403ErrorWhenSearchingForPS } from '@/features/public-site/hooks';
import { isAxiosError } from 'axios';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectAnalysis } from '../_hooks/project';
import { useAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';
import { useAnalysisSheetType } from '../_hooks/useAnalysisSheetType';
import { AnalysisResultsError } from './AnalysisResultsError';
import { AnalysisResultsSkeletonContents } from './AnalysisResultsSkeletonContents';
import { AnalysisResultTable } from './analysis-table/AnalysisResultTable';
import { AnalysisVisualizer } from './analysis-visualizer/AnalysisVisualizer';

export const AnalysisResults: FC = () => {
  const sheetType = useAnalysisSheetType();
  const { searchQueryStates } = useAnalysisPageSearchQueryStates();

  const { t } = useTranslation();

  const projectAnalysis = useProjectAnalysis({
    params: {
      fiscalYearRange: searchQueryStates.fiscalYearRange.join(','),
      analysisAxis:
        searchQueryStates.analysisAxis === 'year'
          ? null
          : searchQueryStates.analysisAxis,
      aggregationType: searchQueryStates.aggregationType,
      analysisTarget: searchQueryStates.analysisTarget,
      aggregationTargetType: searchQueryStates.aggregationTargetType,
      cascader: searchQueryStates.cascader,
      sheetType,
      accountingType: searchQueryStates.accountingType,
      accountingTypeIds: searchQueryStates.accountingTypeIds?.join(',') ?? null,
      implementationMethods:
        searchQueryStates.implementationMethods?.join(',') ?? null,
      projectNumbers: searchQueryStates.projectNumbers
        ? searchQueryStates.projectNumbers
        : null,
      textSearchCriteria: searchQueryStates.isSearchTargetProjectName
        ? 'name'
        : null,
      q: searchQueryStates.q,
      corporateNumber: searchQueryStates.corporateNumber,
      categories: searchQueryStates.categories?.join(',') ?? null,
      organizationIds: searchQueryStates.organizationIds?.join(',') ?? null,
      policyIds: searchQueryStates.policyIds?.join(',') ?? null,
      otherResponsibleOrganizationIds:
        searchQueryStates.otherResponsibleOrganizationIds?.join(',') ?? null,
      expenditureBudgetPurposeIds:
        searchQueryStates.expenditureBudgetPurposeIds?.join(',') ?? null,
      endYear: searchQueryStates.endYear,
      startYear: searchQueryStates.startYear,
    },
  });

  useHandle403ErrorWhenSearchingForPS(projectAnalysis);

  if (projectAnalysis.isError) {
    if (
      isAxiosError(projectAnalysis.error) &&
      ApiErrorCodeLabelModifier[projectAnalysis.error.response?.data.error_code]
    ) {
      return (
        <AnalysisResultsError
          errorMessage={
            ApiErrorCodeLabelModifier[
              projectAnalysis.error.response?.data.error_code
            ]
          }
        />
      );
    }

    return (
      <Box marginTop={16}>
        <ErrorContent reloadable={false} />
      </Box>
    );
  }

  if (projectAnalysis.isPending) {
    return <AnalysisResultsSkeletonContents />;
  }

  return projectAnalysis.data?.length ? (
    <Flex vertical gap={16}>
      <AnalysisVisualizer graphData={projectAnalysis.data} />
      <AnalysisResultTable data={projectAnalysis.data} />
    </Flex>
  ) : (
    <AnalysisResultsError errorMessage={t('no-data')} />
  );
};
